import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Paper,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Alert, Dialog, DialogTitle, DialogContent, CircularProgress, Link, Box, IconButton, FormControl, FormControlLabel, Checkbox, Grid } from "@mui/material";
import { Formik, Form } from "formik";
import useAppDialog from "../../hooks/useAppDialog";
import { DialogResponse, DialogResult } from "../Dialog/dialogTypes";
import { useParams } from "react-router-dom";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";

import * as Yup from "yup";


import useAuth from "../../context/AuthProvider";

import { apiClient } from "../../helper/api";
import { isClientHomeRole, isClientPortalRoleId } from "../../helper/userRoles";
import { isMiscUser, isUserRoleInclude, isStaffUser, formatUtcToLocal } from "../../helper/utils";
import DOMPurify from 'dompurify';
import { UploadedDocumentType } from "../../models/TaxTransactionReportModel";
import Tooltip from '@mui/material/Tooltip';
import Swal from "sweetalert2";
import { AddDocument } from "../../CustomerPanel/AddDocuments";
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce'
import moment from 'moment';
import { CustomerPolicyDetailsModel } from "../../models/CustomerPolicyDetailsModel";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ErrorTagCodeModel } from "../../Pages/ReportTag";

interface MessageDialogProps {
  open: boolean,
  custPolicyID: string,
  viewOnly?: boolean,
  onSubmit?: (val?: any) => void,
  onClose?: () => void,
}

interface MessageProps {
  customerpolicyid: string,
  messageId: string,
  createdBy: string,
  messageType: string,
  message: string,
  createdByName: string,
  isRead: string,
  isEdited: string,
  isDeleted: string,
  dateCreated: string,
  dateModified: string,
  isResponse: string,
  filerName : string,
  createdByRoleId: number,
  roleName: string,
}

interface RequiredDocument {
  srdmId: number;
  documentName: string;
  isRequired: boolean;
}

const sectionTitleSx = {
  color: "primary.main",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.02em",
  display: "block",
  mb: 1.5,
};
const paperSectionSx = {
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: "grey.50",
  overflow: "auto",
};
/** Placeholder line in error tag message templates; must not appear in the sent message. */
const REQUIRED_FILER_NOTES_PLACEHOLDER = "[Required: Filer Notes]";
const OPTIONAL_FILER_NOTES_PLACEHOLDER = "[Optional: Filer Notes]";

const REPLY_COMPOSER_FONT = {
  fontSize: "1rem",
  lineHeight: 1.4375,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
};

const replyComposerContainerSx = {
  border: "2px solid #e2e8f0",
  borderRadius: "8px",
  overflow: "hidden",
  transition: "border-color 0.2s",
  "&:focus-within": { borderColor: "#1976d2" },
  mb: 0.5,
  minHeight: 240,
  display: "flex",
  flexDirection: "column",
};

function normalizeNewlines(s: string): string {
  return s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

/** Template text with placeholder tokens removed (still normalized newlines). */
function stripTemplatePlaceholders(template: string): string {
  return normalizeNewlines(
    template
      .replace(REQUIRED_FILER_NOTES_PLACEHOLDER, "")
      .replace(OPTIONAL_FILER_NOTES_PLACEHOLDER, "")
  );
}

function composeReplyBody(lockedIntro: string, filerNotes: string): string {
  if (!lockedIntro) return filerNotes;
  if (!filerNotes) return lockedIntro;
  if (lockedIntro.endsWith("\n") || filerNotes.startsWith("\n")) {
    return lockedIntro + filerNotes;
  }
  return `${lockedIntro}\n${filerNotes}`;
}

function coerceErrorCodeIds(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    return raw
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v > 0);
  }
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v > 0);
  }
  return [];
}

function findOtherErrorTemplateCode(codes: ErrorTagCodeModel[]): ErrorTagCodeModel | undefined {
  return (
    codes.find((c) => (c.errorTagName ?? "").trim().toLowerCase() === "other type of error") ||
    codes.find((c) => (c.errorTagName ?? "").trim().toLowerCase().includes("other type of error")) ||
    codes.find((c) => (c.errorTagName ?? "").trim().toLowerCase().includes("other"))
  );
}

/** True when content appears to be HTML (e.g. from TinyMCE), not plain text with newlines. */
function isLikelyHtmlMessage(s: string): boolean {
  return /<\/?[a-z][\s\S]*?>/i.test(s);
}

function sanitizePlainTextMessage(s: string): string {
  return DOMPurify.sanitize(s, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/** Fixed intro text in a template, before the required filer notes line. */
function getPrefixBeforeRequiredPlaceholder(template: string): string {
  const idx = template.indexOf(REQUIRED_FILER_NOTES_PLACEHOLDER);
  if (idx < 0) return "";
  return template.slice(0, idx);
}

/**
 * Split template into locked intro (before required filer notes) vs editable remainder.
 * When the template has no required placeholder, the full stripped template is editable.
 */
function splitTemplateForComposer(template: string): { lockedIntro: string; initialNotes: string } {
  const full = stripTemplatePlaceholders(template);
  if (!template.includes(REQUIRED_FILER_NOTES_PLACEHOLDER)) {
    return { lockedIntro: "", initialNotes: full };
  }
  const prefix = normalizeNewlines(getPrefixBeforeRequiredPlaceholder(template));
  if (full.startsWith(prefix)) {
    return { lockedIntro: prefix.trimEnd(), initialNotes: full.slice(prefix.length) };
  }
  const locked = prefix.trimEnd();
  if (full.startsWith(locked)) {
    return { lockedIntro: locked, initialNotes: full.slice(locked.length) };
  }
  return { lockedIntro: "", initialNotes: full };
}

/** Validation for split composer (locked intro + editable notes) and fallback single-field mode. */
function getComposerFilerNotesError(
  filerNotes: string,
  lockedIntro: string,
  insertTemplateValue: number | "",
  errorTagCodes: ErrorTagCodeModel[]
): string | null {
  const composed = composeReplyBody(lockedIntro, filerNotes);
  if (composed.includes(REQUIRED_FILER_NOTES_PLACEHOLDER)) {
    return "Replace the template placeholder with your filer notes before sending.";
  }
  // Locked intro only exists when the template includes required filer notes; editable part must be non-empty.
  if (lockedIntro.length > 0) {
    if (!filerNotes.trim()) {
      return "Add filer notes before sending. Describe what the client needs to do after the introduction.";
    }
    return null;
  }

  // Fallback behavior when no locked intro is present.
  if (typeof insertTemplateValue === "number") {
    const et = errorTagCodes.find((c) => c.errorTagCodeId === insertTemplateValue);
    const template = et?.messageTemplate ?? "";
    if (template.includes(REQUIRED_FILER_NOTES_PLACEHOLDER) && !filerNotes.trim()) {
      return "Add filer notes before sending. Describe what the client needs to do after the introduction.";
    }
  }
  return null;
}

const MessageDialog: React.FC<MessageDialogProps> = ({ open, custPolicyID, viewOnly = false, onSubmit, onClose }) => {
  const [messages, setMessages] = useState<MessageProps[]>([]);
  const [loading, setLoading] = useState(false);
  /** Non-editable template intro when the selected template includes required filer notes. */
  const [lockedTemplateIntro, setLockedTemplateIntro] = useState("");
  const [UploadedDocument, setUploadedDocument] = useState<UploadedDocumentType[]>([]);
  const [revertedDocument, setRevertedDocument] = React.useState<UploadedDocumentType[]>([]);
  const [openDoc, setOpenDoc] = useState(false);
  const [itemList, setItemList] = useState<RequiredDocument[]>([]);
  const [policyDetail, setPolicyDetail] = useState<CustomerPolicyDetailsModel>({});
  const [errorTagCodes, setErrorTagCodes] = useState<ErrorTagCodeModel[]>([]);
  const [insertTemplateValue, setInsertTemplateValue] = useState<number | ''>('');
  const [selectedErrorCodes, setSelectedErrorCodes] = useState<number[]>([]);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  // const [loading, setloading] = useState(true);
  const params = useParams();
  const { openDialog_Message } = useAppDialog()
  const [getPolicyStatus, setPolicyStatus] = React.useState<string>('');
  const { user } = useAuth();
  const token = user?.token;
  const sanitizeHTML = (html: any) => DOMPurify.sanitize(html);

  const [revertDocument, setRevertDocument] = useState<{ documentTypeId: number; customerPolicyDocumentId: number; isRevert: boolean}[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const validationSchema = useMemo(
    () =>
      Yup.object({
        filerNotes: Yup.string()
          .required("Reply is required")
          .test("filer-notes", "", function (value) {
            const err = getComposerFilerNotesError(
              value ?? "",
              lockedTemplateIntro,
              insertTemplateValue,
              errorTagCodes
            );
            if (err) return this.createError({ message: err });
            return true;
          }),
      }),
    [insertTemplateValue, errorTagCodes, lockedTemplateIntro]
  );

  const UploadDocument = () => {
    setOpenDoc(true);
  }

  const handleClose = () => {
    getUploadedDocuments()
    fetchRequiredDocument()
    setOpenDoc(false)
  };
  

  const getUploadedDocuments = () => {
    apiClient(`/customer/getUploadedDocuments?customerPolicyId=${custPolicyID}`,
      {
        method: "GET",
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        })
             }).then((res) => {
         try {
           if (!res) {
             setUploadedDocument([]);
             return;
           }
           
           if (res && res.data && Array.isArray(res.data)) {
             setUploadedDocument(res.data);
           } else {
             setUploadedDocument([]);
           }
         } catch (error) {
           console.error('Error processing uploaded documents response:', error);
           setUploadedDocument([]);
         }
       })
  }
  const getRevertedDocuments = () => {
    setLoading(true)
    return apiClient(`/customer/getRevertedDocuments?customerPolicyId=${custPolicyID}`,
    {
        method: "GET",
        headers: new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        })
    }).then((res) => {
        if (!res || !res.data || !Array.isArray(res.data)) {
            setRevertedDocument([]);
            setLoading(false);
            return;
        }
        setRevertedDocument(res.data.filter((item: UploadedDocumentType) => item.documentTypeId != 0));
        setLoading(false)
    }).catch((error) => {
        setRevertedDocument([]);
        setLoading(false);
    })
  }
  const handleSendMessage = async (CustomerPolicyId: number, status: string) => {
    try {
        setLoading(true);
        openDialog_Message({
            custPolId: String(CustomerPolicyId)
        }).then((res: DialogResponse) => {
            if (res.dialogResult === DialogResult.OK) {
  
            }
            return res
        })
        setLoading(false);
    } catch (error) {
        setLoading(false);
    }
  };
  function RevertDocument(
    documentTypeId: number,
    documentName: string | undefined,
    customerPolicyDocumentId: number
  ) {
    const isRevertExist = revertDocument.some((val) => val.documentTypeId === documentTypeId);
    if (!isRevertExist || true) {
        setRevertDocument((prev) => [...prev,  { documentTypeId, customerPolicyDocumentId, isRevert: true},]);
    } else {
        setRevertDocument((prev) =>
            prev.filter((val) => val.documentTypeId !== documentTypeId || val.customerPolicyDocumentId !== customerPolicyDocumentId)
        );
    }
  }
  const handleRevert = async (documentDetails: any, isAll: boolean) => {
     Swal.fire({
         title:  'Send Back to Client',
         text: `Are you sure you want to Send Back to Client ?`,
         icon: 'question',
         showCancelButton: true,
         confirmButtonColor: '#007BFF',
         cancelButtonColor: '#6c757d',
         confirmButtonText: 'Confirm',
         cancelButtonText: 'Cancel',
         padding: '20px',
         backdrop: true,
         customClass: {
             popup: 'custom-swal-popup',
             confirmButton: 'btn-radius',
             cancelButton: 'btn-radius',
         },
         width: '500px',
     })
     .then(async (result) => {
         if (result.isConfirmed) {
             try {
                 setLoading(true);
                 
                 // Extract customerPolicyDocumentIds from documentDetails
                 const customerPolicyDocumentIds = documentDetails
                     .map((doc: any) => doc.customerPolicyDocumentId)
                     .join(',');
                 
                 // Create payload for the new API
                 const payload = {
                     customerPolicyDocumentIds: customerPolicyDocumentIds,
                     customerPolicyId: Number(custPolicyID)
                 };
                 
                 console.log('Reverting documents with payload:', payload);
                 
                                   const res = await apiClient(`/customer/revertAllDocuments`, {
                      method: 'POST',
                      headers: new Headers({
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                      }),
                      body: JSON.stringify(payload)
                  });
                 
                 if (res.resp == 0 || res.status === true) {
                     Swal.fire({
                         title: 'Success',
                         text: 'Documents have been sent back to client.',
                         icon: 'success',
                         confirmButtonColor: '#3085d6',
                         confirmButtonText: 'OK'
                     });
                     
                     // Clear revert state
                     setRevertDocument([]);
                     
                     // Refresh the uploaded and reverted documents to show the updated state
                     setTimeout(() => {
                         getUploadedDocuments();
                         getRevertedDocuments();
                     }, 500);
                     
                     // Send message after successful revert
                     if (custPolicyID && custPolicyID !== 'undefined' && custPolicyID !== 'null' && custPolicyID !== '') {
                         handleSendMessage(Number(custPolicyID), "Waiting on Client");
                     }
                 } else {
                     Swal.fire({
                         title: 'Error',
                         text: res.message || 'Failed to revert documents',
                         icon: 'error',
                         confirmButtonColor: '#dc3545',
                         confirmButtonText: 'OK'
                     });
                 }
             } catch (error: any) {
                 console.error('Error reverting documents:', error);
                 Swal.fire({
                     title: 'Error',
                     text: `Failed to revert documents: ${error?.message || 'Unknown error'}`,
                     icon: 'error',
                     confirmButtonColor: '#dc3545',
                     confirmButtonText: 'OK'
                 });
             } finally {
                 setLoading(false);
             }
         }
     });
  };
  const fetchRequiredDocument = () => {
    apiClient(`/customer/getRequiredDocuments?customerPolicyId=${custPolicyID}&all=0`, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
             .then(responseJson => {
         try {
           if (responseJson && responseJson.data && Array.isArray(responseJson.data) && responseJson.data.length > 0) {
             setItemList(responseJson.data);
           } else {
             setItemList([]);
           }
         } catch (error) {
           console.error('Error processing required documents response:', error);
           setItemList([]);
         }
       })
      .catch(error => {
        setItemList([]);
        setLoading(false);
      });
  };

  const createMessage = async (filerNotes: string, lockedIntro: string): Promise<boolean> => {
     const reply = composeReplyBody(lockedIntro, filerNotes);
     const filerNotesErr = getComposerFilerNotesError(
       filerNotes,
       lockedIntro,
       insertTemplateValue,
       errorTagCodes
     );
     if (filerNotesErr) {
       Swal.fire({
         title: "Filer notes required",
         text: filerNotesErr,
         icon: "warning",
         confirmButtonColor: "#3085d6",
         confirmButtonText: "OK",
       });
       return false;
     }

     // Validate customerPolicyId before sending
     if (!custPolicyID || custPolicyID === 'undefined' || custPolicyID === 'null') {
       console.error('Invalid custPolicyID:', custPolicyID);
       Swal.fire({
         title: 'Error',
         text: 'Invalid Customer Policy ID. Please refresh the page and try again.',
         icon: 'error',
         confirmButtonColor: '#dc3545',
         confirmButtonText: 'OK'
       });
       return false;
     }

     const policyStatus = policyDetail.status || ''
     const homeState = policyDetail.homeState || ''
     if (!homeState) {
       // Show an error when the HomeState is not defined.
       console.error('Invalid HomeState:', homeState);
       Swal.fire({
         title: 'Error',
         text: 'Invalid Customer Home State. Please refresh the page and try again.',
         icon: 'error',
         confirmButtonColor: '#dc3545',
         confirmButtonText: 'OK'
       });
      return false;
     }

    //  const filerName = user?.userFirstName + ' ' + user?.userlastName

     const formData = new FormData();
     formData.append("customerPolicyId", custPolicyID.toString());
     formData.append("message", reply);
     formData.append("isResponse", 'true');
     formData.append("policyStatus", policyStatus)
     formData.append("homeState", homeState)
     const errorTagCodeIdsPayload =
       selectedErrorCodes.length > 0
         ? selectedErrorCodes.join(',')
         : typeof insertTemplateValue === 'number'
           ? String(insertTemplateValue)
           : '';
     if (errorTagCodeIdsPayload) {
       formData.append('errorTagCodeIds', errorTagCodeIdsPayload);
     }
    //  formData.append("filerName", filerName)

     setLoading(true);
     let success = false;
     await apiClient(`/messages/createMessage/`, {
       method: "POST",
       headers: new Headers({
         Authorization: `Bearer ${token}`,
       }),
       body: formData,
     }).then((res) => {
       if (!res) {
         Swal.fire({
           title: 'Error',
           text: 'No response received from server',
           icon: 'error',
           confirmButtonColor: '#dc3545',
           confirmButtonText: 'OK'
         });
         return;
       }

       if (res.status === false) {
         // Handle API error response
         Swal.fire({
           title: 'Error',
           text: res.message || 'Failed to send message',
           icon: 'error',
           confirmButtonColor: '#dc3545',
           confirmButtonText: 'OK'
         });
         return;
       }

       // Success case
       success = true;
       Swal.fire({
         title: 'Message Sent Successfully',
         text: res.data || 'Your message has been sent.',
         icon: 'success',
         confirmButtonColor: '#3085d6',
         confirmButtonText: 'OK'
       });
     }).catch((error) => {
       console.error('Error creating message:', error);
       Swal.fire({
         title: 'Error',
         text: 'Failed to send message. Please try again.',
         icon: 'error',
         confirmButtonColor: '#dc3545',
         confirmButtonText: 'OK'
       });
     }).finally(() => {
       setLoading(false);
     });
     return success;
  }

  const handleSubmit = async (values: { filerNotes: string }, { setSubmitting, setFieldValue }: any) => {
    const composedForCallback = composeReplyBody(lockedTemplateIntro, values.filerNotes);
    const success = await createMessage(values.filerNotes, lockedTemplateIntro);
    setSubmitting(false);
    if (success) {
      setFieldValue("filerNotes", "", false);
      setLockedTemplateIntro("");
      setInsertTemplateValue("");
      setSelectedErrorCodes([]);
      fetchMessages();
      onSubmit?.(composedForCallback);
    }
  };

  const fetchMessages = () => {
    apiClient(`/messages/getMessages?customerPolicyId=${custPolicyID}`, {
      method: "GET",
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    }).then((responseJson) => {
      try {
        if (!responseJson) {
          setMessages([]);
          return;
        }
        
        // Safely check the response structure
        const responseData = responseJson?.data;
        if (!responseData) {
          setMessages([]);
          return;
        }
        
        const messagesData = responseData.getMessages_Respdata;
        
        // Check if messagesData exists and is an array (any length is fine)
        if (messagesData && Array.isArray(messagesData)) {
          setMessages(messagesData);
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Error processing messages response:', error);
        setMessages([]);
      }
    }).catch((error) => {
      console.error('Error fetching messages:', error);
      setMessages([]); // Set empty messages on error
    }).finally(() => {
      setLoading(false); // Stop loading spinner
      setMessagesLoaded(true);
    });
  }

  const fetchPolicyDetail = () => {
    apiClient(`/PolicyDashBoard/getCustomerPolicyDetails?customerPolicyId=${custPolicyID}`,{
      method: "GET",
      headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
      })
    }).then((res) => {
      if (!res) return
      console.log(res?.data)
      setPolicyDetail(res?.data)
    })
  }

  const fetchErrorTagCodes = () => {
    setLoading(true);
    apiClient('/ReportTags/getAllErrorTags', {
        method: "GET",
        headers: new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        })
    }).then((responseJson) => {
        if (!responseJson) return;
        setErrorTagCodes(responseJson.data);
    }).catch(error => {
        console.error("Error fetching error tag codes:", error)
    }).finally(() => {
        setLoading(false);
    });
  };

  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInsertTemplateValue('');
      setSelectedErrorCodes([]);
      setLockedTemplateIntro("");
      setMessagesLoaded(false);
      return;
    }

    // Debug logging for custPolicyID
    console.log('useEffect triggered with custPolicyID:', custPolicyID, 'type:', typeof custPolicyID);

    setLoading(true);
    getUploadedDocuments();
    getRevertedDocuments();
    fetchRequiredDocument();
    fetchMessages();
    fetchPolicyDetail();
    fetchErrorTagCodes();
  }, [open, custPolicyID]);

  const isReply = () => {
    if (isStaffUser(user?.userRoles)) {
      return true
    } else {
      return messages[0]?.isResponse ? true : false
    }
  }

  const isUserMessage = (userId: any) => user?.userId.toString() === userId.toString() ? true : false

  const isAdminMessage = (message: MessageProps) => {
    return !isClientPortalRoleId(message.createdByRoleId);
  }

  const userRoleList = useMemo(() => {
    return user?.userRoles?.split(",") ?? [];
  }, [user?.userRoles]);

  const isSuperAdminUser = useMemo(() => {
    return isUserRoleInclude(userRoleList, ["Super Admin"]);
  }, [userRoleList]);

  const isMiscScopedDeleteUser = useMemo(() => {
    return isUserRoleInclude(userRoleList, ["MISC State Reporter", "MISC Filer"]);
  }, [userRoleList]);

  const canDeleteMessage = (message: MessageProps) => {
    if (viewOnly || !message || !isAdminMessage(message)) return false;
    if (isSuperAdminUser) return true;
    if (!isMiscScopedDeleteUser) return false;

    const currentUserId = user?.userId;
    const messageCreatedBy = message?.createdBy;
    if (currentUserId == null || messageCreatedBy == null) return false;

    return currentUserId.toString() === messageCreatedBy.toString();
  };

  // Helper function to determine if user can delete documents (admin or client users)
  const canDeleteDocuments = () => {
    if (!user?.userRoles) return false;
    const roles = user.userRoles.split(",");
    return roles.some(role => 
      ['Super Admin', 'Client Admin', 'Client Accounting', 'Client Operations', 'Client Processors'].includes(role.trim())
    );
  };

  // Helper function to display MISC user names as "miscstaff" for client users
  const getDisplayName = (uploadedBy: string, uploadedByRoleId?: number) => {
    const name = (uploadedBy ?? "").trim() || "-";
    if (!user?.userRoles) return name;

    if (isClientHomeRole(user.userRoles)) {
      return isClientPortalRoleId(uploadedByRoleId) ? name : "MISC team";
    }
    return name;
  };

  const groupedRows = useMemo(() => {
    // Group uploaded documents
    const uploadGroups = new Map<string, UploadedDocumentType[]>();
    UploadedDocument.forEach((doc) => {
      const fileName = Array.isArray((doc as any).documentName) ? ((doc as any).documentName?.[0] || '') : (doc as any).documentName;
      const key = `${doc.blob_Address}::${fileName}`;
      const arr = uploadGroups.get(key) || [];
      arr.push(doc);
      uploadGroups.set(key, arr);
    });

    // Group reverted documents
    const revertGroups = new Map<string, UploadedDocumentType[]>();
    revertedDocument.forEach((doc) => {
      const fileName = Array.isArray((doc as any).documentName) ? ((doc as any).documentName?.[0] || '') : (doc as any).documentName;
      const key = `${doc.blob_Address}::${fileName}`;
      const arr = revertGroups.get(key) || [];
      arr.push(doc);
      revertGroups.set(key, arr);
    });

    // Process uploaded documents (latest first)
    const uploadedRows = Array.from(uploadGroups.values()).map((docs, idx) => {
      const first = docs[0] as any;
      const fileName = Array.isArray(first.documentName) ? (first.documentName?.[0] || '') : first.documentName;
      const docTypeNames = Array.from(new Set(
        docs.map((d) => (d as any).name).filter(Boolean)
      )).join(', ');
      const uploadedAt = first.dateUploadedAt ? formatUtcToLocal(first.dateUploadedAt, 'MM/DD/YYYY HH:mm') : '-';
      const uploadedBy = getDisplayName(first.uploadedByFullName || first.uploadedBy || '-', first.uploadedByRoleId);
      const sizeText = typeof first.fileSizeKB === 'number' ? `${first.fileSizeKB} KB` : '-';
      const pagesText = typeof first.numberOfPages === 'number' ? first.numberOfPages : '-';
      
      return { 
        first, 
        fileName, 
        docTypeNames, 
        allDocuments: docs,
        uploadedAt, 
        uploadedBy, 
        sizeText, 
        pagesText,
        isReverted: false
      };
    }).sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

    // Process reverted documents (latest first)
    const revertedRows = Array.from(revertGroups.values()).map((docs, idx) => {
      const first = docs[0] as any;
      const fileName = Array.isArray(first.documentName) ? (first.documentName?.[0] || '') : first.documentName;
      const docTypeNames = Array.from(new Set(
        docs.map((d) => (d as any).name).filter(Boolean)
      )).join(', ');
      const uploadedAt = first.dateUploadedAt ? formatUtcToLocal(first.dateUploadedAt, 'MM/DD/YYYY HH:mm') : '-';
      const uploadedBy = getDisplayName(first.uploadedByFullName || first.uploadedBy || '-', first.uploadedByRoleId);
      const sizeText = typeof first.fileSizeKB === 'number' ? `${first.fileSizeKB} KB` : '-';
      const pagesText = typeof first.numberOfPages === 'number' ? first.numberOfPages : '-';
      
      return { 
        first, 
        fileName, 
        docTypeNames, 
        allDocuments: docs,
        uploadedAt, 
        uploadedBy, 
        sizeText, 
        pagesText,
        isReverted: true
      };
    }).sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

    // Return uploaded documents first, then reverted documents
    return [...uploadedRows, ...revertedRows];
  }, [UploadedDocument, revertedDocument, user]);



  const handleDeleteMessage = (message: MessageProps) => {
    
    Swal.fire({
      title: 'Are you sure, you want to delete this message?',
      // text: "You won't be able to revert this!",
      // icon: null, // Remove the icon property
      iconHtml: '<svg width="70" height="70" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="31" cy="31" r="31" fill="#02A4E3"/><path d="M31.0003 25.6667V31M31.0003 36.3334H31.0137M44.3337 31C44.3337 38.3638 38.3641 44.3334 31.0003 44.3334C23.6365 44.3334 17.667 38.3638 17.667 31C17.667 23.6362 23.6365 17.6667 31.0003 17.6667C38.3641 17.6667 44.3337 23.6362 44.3337 31Z" stroke="white" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      showCancelButton: true,
      confirmButtonColor: '#02A4E3',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Delete',
      customClass: {
        confirmButton: 'btn-radius',
        cancelButton: 'btn-radius',
        popup: 'card-radius'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true)
        apiClient("/messages/deleteMessageById?messageId=" + `${message.messageId}`,
          {
            headers: new Headers({
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            }),
            method: "Delete",
          }
        ).then((res) => {
          if (!res) return
          if (res.data == "success") {
            Swal.fire(
              'Deleted!',
              'Your file has been deleted.',
              'success')
              .then(() => {
                //  goBack();
                // fetchData();
              }
            )
          }
          // setOpen(false);
        }).finally(() => {
          setMessages(v => v.filter(m => m.messageId !== message.messageId))
          setLoading(false);
        });
      }
      setLoading(false);
    })
  }

  const handleDeleteDocument = async (rowData: any) => {
    const resDlg = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      iconHtml: '<svg width="70" height="70" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="31" cy="31" r="31" fill="#02A4E3"/><path d="M31.0003 25.6667V31M31.0003 36.3334H31.0137M44.3337 31C44.3337 38.3638 38.3641 44.3334 31.0003 44.3334C23.6365 44.3334 17.667 38.3638 17.667 31C17.667 23.6362 23.6365 17.6667 31.0003 17.6667C38.3641 17.6667 44.3337 23.6362 44.3337 31Z" stroke="white" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      showCancelButton: true,
      confirmButtonColor: '#02A4E3',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Delete',
      customClass: {
        confirmButton: 'btn-radius',
        cancelButton: 'btn-radius',
        popup: 'card-radius'
      }
    });

    if (!resDlg.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      // Delete all documents in this row
      const deletePromises = rowData.allDocuments.map((doc: UploadedDocumentType) => 
        apiClient(`/customer/DeleteUploadedDocument?customerPolicyDocumentId=${doc.customerPolicyDocumentId}`, {
          headers: new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }),
          method: "Delete",
        })
      );

      await Promise.all(deletePromises);

      // Refresh data after successful deletion
      await Promise.all([
        getUploadedDocuments(),
        getRevertedDocuments(),
        fetchRequiredDocument()
      ]);

      Swal.fire({
        icon: 'success',
        title: 'Deleted Successfully!',
        text: `The file "${rowData.fileName}" and its document types have been successfully deleted.`,
        confirmButtonText: 'OK'
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: 'Some documents could not be deleted. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  }

  function ViewDocuments(props: { item: UploadedDocumentType }) {
    const { item } = props;

    return (
      <div className="box-style col-lg-12">
        <div style={{ marginTop: "8px" }}>
          <Tooltip
            title={`Download ${item.documentName}`}
            arrow
          >
            <Link
              href={item.blob_Address}
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
              target="_blank"
            >
              <div
                role="presentation"
                tabIndex={0}
                style={{
                  flex: "1 1 0%",
                  display: "flex",
                  alignItems: "center",
                  padding: "10px",
                  borderWidth: "1px",
                  borderRadius: "8px",
                  borderColor: "rgb(65, 114, 159)",
                  borderStyle: "dashed",
                  backgroundColor: "rgb(250, 250, 250)",
                  color: "rgb(65, 114, 159)",
                  outline: "none",
                  transition: "border 0.24s ease-in-out",
                  cursor: "pointer",
                }}
              >
                <span style={{ marginRight: "8px" }}>
                  <p style={{ margin: 0 }}>{item.name}</p>
                </span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.25 13.75V17.4167C19.25 17.9029 19.0568 18.3692 18.713 18.713C18.3692 19.0568 17.9029 19.25 17.4167 19.25H4.58333C4.0971 19.25 3.63079 19.0568 3.28697 18.713C2.94315 18.3692 2.75 17.9029 2.75 17.4167V13.75M6.41667 9.16667L11 13.75M11 13.75L15.5833 9.16667M11 13.75V2.75"
                    stroke="#02A4E3"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </div>
            </Link>
          </Tooltip>
          
        {/* Document name display */}
        <div style={{ display: "flex", flexDirection: "column", marginTop: "5px" }}>
          <p
            style={{
              height: "20px",
              textAlign: "start",
              margin: "2px 0",
              color: "#555",
              fontSize: "0.9rem",
            }}
          >
            {item.documentName}
          </p>
          <div>
            <p style={{ marginTop: "2px", textAlign: 'start' }}>Date Uploaded: {item.dateUploadedAt ? formatUtcToLocal(item.dateUploadedAt, 'MM/DD/YYYY HH:mm') : ''}
            </p>
          </div>
        </div>

        {/* Document type with download link at the bottom */}
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "95%",
            height: "95%",
            maxHeight: "95vh",
            margin: "auto",
            borderRadius: 2,
            boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
          },
        }}
        sx={{ "& .MuiDialog-paper": { overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 2,
            px: 3,
            background: "linear-gradient(135deg, #0d4a63 0%, #15607a 100%)",
            color: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <MailOutlineOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
            <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: "0.02em" }}>
              Inbox
            </Typography>
            {!!policyDetail.policyNumber && (
              <Typography component="span" variant="body1" sx={{ opacity: 0.95, ml: 1 }}>
                Policy: {policyDetail.policyNumber}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "rgba(255,255,255,0.9)",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
              },
            }}
            aria-label="Close dialog"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "grey.50", px: { xs: 2, sm: 3 }, py: 3, overflow: "auto" }}>
          {(policyDetail?.isPrePortalHistorical === true || policyDetail?.isPrePortalHistorical === 1) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No message history or document history exists in this system because this is historical data prior to moving to the new system
            </Alert>
          )}
          <Formik
            key={`reply-${custPolicyID}-${open}`}
            initialValues={{ filerNotes: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting, handleChange, handleBlur, errors, touched, setFieldValue }) => {
              const isFirstMessage = messagesLoaded && messages.length === 0;
              const isNYFiledOnBinder = policyDetail.homeState === 'NY' &&
                !!policyDetail.policySubStatus?.toLowerCase().includes('binder');
              const requiresErrorCode = isFirstMessage && isStaffUser(user?.userRoles) && !isNYFiledOnBinder;
              const composedReply = composeReplyBody(lockedTemplateIntro, values.filerNotes ?? "");
              const sendDisabled = Boolean(
                isSubmitting ||
                !composedReply.trim().length ||
                getComposerFilerNotesError(
                  values.filerNotes ?? "",
                  lockedTemplateIntro,
                  insertTemplateValue,
                  errorTagCodes
                ) !== null ||
                (requiresErrorCode && selectedErrorCodes.length === 0));
              return (
              <Form style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ display: "flex", gap: 3, flex: 1, py: 2, height: "65%" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="overline" sx={sectionTitleSx}>Message</Typography>
                    <Paper variant="outlined" sx={{ ...paperSectionSx, height: "91%", minHeight: 200 }}>
                      <table className="table table-striped table-hover"
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                        }}
                      >
                        <tbody>
                          {messages && messages?.length > 0 ? (
                            messages.map((message, index) => (
                              <tr key={index}>
                                <td
                                  style={{
                                    fontWeight: "bold",
                                    padding: "8px",
                                    verticalAlign: "top",
                                    borderBottom: "1px solid #ddd",
                                    whiteSpace: "nowrap",
                                    width: "200px",
                                  }}
                                >
                                  {isClientHomeRole(user?.userRoles)
                                    ? isClientPortalRoleId(message.createdByRoleId)
                                      ? message.createdByName || "Unknown"
                                      : "MISC team"
                                    : message.createdByName || "Unknown"}
                                </td>
                                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>
                                  {/* Render HTML content or plain text */}
                                  {/* {message.message ? (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: message.message,
                                      }}
                                      style={{
                                        fontSize: "0.9rem",
                                        color: "#333",
                                        lineHeight: "1.5",
                                      }}
                                    />
                                  ) : (
                                    <span>No message content</span>
                                  )} */}
                                  <div
                                    style={{
                                      fontSize: "0.9rem",
                                      color: "#333",
                                      lineHeight: "1.5",
                                      maxWidth: "100%",
                                      overflowX: "auto",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    {message.message ? (
                                      isLikelyHtmlMessage(message.message) ? (
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeHTML(message.message),
                                          }}
                                          style={{
                                            overflowX: "auto",
                                            whiteSpace: "normal",
                                            wordWrap: "break-word",
                                            maxWidth: "100%",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            overflowX: "auto",
                                            whiteSpace: "pre-wrap",
                                            wordWrap: "break-word",
                                            wordBreak: "break-word",
                                            maxWidth: "100%",
                                          }}
                                        >
                                          {normalizeNewlines(sanitizePlainTextMessage(message.message))}
                                        </div>
                                      )
                                    ) : (
                                      <span>No message content</span>
                                    )}
                                  </div>
                                  {/* Render the date */}
                                   <span style={{ fontSize: "0.85em", color: "#888" }}>
                                     {message.dateModified
                                       ? formatUtcToLocal(message.dateModified, 'MM/DD/YYYY, hh:mm:ss A')
                                       : "Unknown date"}
                                   </span>
                                </td>
                                
                                {canDeleteMessage(message) && <td style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #ddd",
                                  width: "30px"
                                }}>
                                  <div
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleDeleteMessage(message)}
                                  >
                                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 20, color: "#d32f2f" }} />
                                  </div>
                                </td>}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={2} style={{ textAlign: "center", padding: "8px" }}>
                                No messages
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </Paper>
                  </Box>

                  {/* Right Side: Attachments — shown in viewOnly with uploads/deletes disabled */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="overline" sx={sectionTitleSx}>Attachments</Typography>
                    <Paper variant="outlined" sx={{ ...paperSectionSx, height: "91%", minHeight: 200 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                          <Typography variant="overline" sx={{ ...sectionTitleSx, color: "text.primary", display: "block", mb: 1.5 }}>History</Typography>
                          {!viewOnly && itemList?.length > 0 && (
                            <Button
                              type="button"
                              variant="contained"
                              size="medium"
                              startIcon={<CloudUploadOutlinedIcon />}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                UploadDocument();
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                backgroundColor: "#2099d2",
                                "&:hover": { backgroundColor: "#176990" },
                              }}
                            >
                              Upload Document
                            </Button>
                          )}
                        </Box>
                         {/* DataGrid for show Document  */}
                         {groupedRows && groupedRows.length > 0 ? (
                           <Box sx={{ width: '100%', mb: 2 }}>
                             <DataGridPro
                               autoHeight
                               rows={groupedRows.map((r, idx) => ({
                                 id: `${r.first.customerPolicyDocumentId}-${idx}`,
                                 fileName: r.fileName,
                                 docTypeNames: r.docTypeNames,
                                 uploadedAt: r.uploadedAt,
                                 uploadedBy: r.uploadedBy,
                                 sizeText: r.sizeText,
                                 pagesText: r.pagesText,
                                 first: r.first,
                                 allDocuments: r.allDocuments,
                                 isReverted: r.isReverted
                               }))}
                               columns={([
                                 { field: 'fileName', headerName: 'File Name', flex: 1, minWidth: 200, renderCell: (params) => (
                                   <Link href={params.row.first.blob_Address} target="_blank" sx={{textAlign: 'initial',width: '100%', textDecoration: 'none', overflow: 'initial', whiteSpace: 'break-spaces',lineHeight:'normal' }}>{params.value || '-'}</Link>
                                 ) },
                                 { field: 'docTypeNames', headerName: 'Document Types', flex: 1, minWidth: 310, maxWidth: 400, renderCell: (params) => (
                                   <Box sx={{ textAlign: 'initial', width: '100%', overflow: 'initial', textOverflow: 'ellipsis', whiteSpace: 'break-spaces',lineHeight:'normal' }}>
                                     {params.value || '-'}
                                   </Box>
                                 ) },
                                 { field: 'uploadedAt', headerName: 'Upload Date', flex: 0.8, minWidth: 160 },
                                 { field: 'uploadedBy', headerName: 'Uploaded By', flex: 0.8, minWidth: 160 },
                                 { field: 'sizeText', headerName: 'File Size', flex: 0.6, minWidth: 120 },
                                 ...(canDeleteDocuments() && !viewOnly ? [{
                                   field: 'actions', 
                                   headerName: 'Actions', 
                                   sortable: false, 
                                   filterable: false, 
                                   flex: 0.5, 
                                   minWidth: 120, 
                                   renderCell: (params: any) => (
                                     <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                       {/* Delete Button */}
                                       <Tooltip title="Delete Document" arrow>
                                         <IconButton 
                                           color="error" 
                                           onClick={() => {
                                             handleDeleteDocument(params.row)
                                           }}
                                         >
                                           <DeleteOutlineOutlinedIcon sx={{ fontSize: 20, color: "#d32f2f" }} />
                                         </IconButton>
                                       </Tooltip>
                                     </div>
                                   )
                                 }] : [])
                               ]) as GridColDef[]}
                               pagination
                               rowsPerPageOptions={[5, 10, 25]}
                               pageSize={rowsPerPage}
                               onPageSizeChange={(newPageSize: number) => setRowsPerPage(newPageSize)}
                               disableSelectionOnClick
                               disableColumnMenu
                               sx={{
                                 borderRadius: '10px',
                                 overflow: 'hidden',
                                 fontFamily: "'Proxima Nova', sans-serif",
                                 fontSize: '14px',
                                 '& .MuiDataGrid-main': {
                                   overflow: 'hidden'
                                 },
                                 '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f8fafc',
                                    borderBottom: '2px solid #e2e8f0',
                                    minHeight: '52px !important',
                                    maxHeight: '52px !important',
                                  },
                                  '& .MuiDataGrid-columnHeader': {
                                    '&:focus, &:focus-within': { outline: 'none' },
                                  },

                                  '& .MuiDataGrid-row': {
                            transition: 'background-color 0.15s ease',
                            '&:nth-of-type(even)': {
                                backgroundColor: '#fafbfc',
                            },
                            '&:hover': {
                                backgroundColor: '#f1f5f9 !important',
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                            fontSize: '14px',
                            color: '#334155',
                            overflow: 'visible',
                            whiteSpace: 'normal',
                            lineHeight: 'normal',
                            wordWrap: 'break-word',
                            textOverflow: 'clip',
                            padding: '8px 16px',
                            '&:focus': { outline: 'none' },
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            marginBottom: '0px !important',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            border: 'none',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '1px solid #e2e8f0',
                            backgroundColor: '#f8fafc',
                            minHeight: 56,
                        },
                        '& .MuiTablePagination-root': {
                            color: '#64748b',
                            fontSize: '13px',
                        },
                        '& .MuiTablePagination-select': {
                            fontSize: '13px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                        },
                        '& .MuiIconButton-root': {
                            color: '#64748b',
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                            },
                        },
                                }}
                               getRowClassName={(params) => {
                                 return params.row.isReverted ? 'reverted-row' : '';
                               }}
                             />
                           </Box>
                         ) : (
                           <Box sx={{ 
                             width: '100%', 
                             textAlign: 'center', 
                             py: 4, 
                             color: '#666',
                             fontFamily: "'Proxima Nova', sans-serif",
                             fontSize: '16px'
                           }}>
                             No documents available
                           </Box>
                         )}
                    </Paper>
                  </Box>
                </Box>

                {/* Reply Section - Hidden in view only mode */}
                {!viewOnly && (
                  <div style={{ flex: "0 0 auto", marginTop: "0", marginBottom: "0" }}>
                    <h3 style={{ color: "#00A9E0", marginTop: "25px", marginBottom: "8px", fontWeight: "bold", fontSize: '20px' }}>REPLY</h3>
                    {isUserRoleInclude(user?.userRoles.split(","), ["Super Admin", "MISC Filer", "MISC State Reporter"]) && (
                      <FormControl fullWidth sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                          {requiresErrorCode ? 'Error Code(s) — required' : 'Error Code(s) — optional'}
                        </Typography>
                        <Grid container spacing={0} columnSpacing={2}>
                          {[
                            errorTagCodes.slice(0, Math.ceil(errorTagCodes.length / 2)),
                            errorTagCodes.slice(Math.ceil(errorTagCodes.length / 2)),
                          ].map((columnCodes, colIndex) => (
                            <Grid item xs={12} sm={6} key={colIndex}>
                              {columnCodes.map((et) => (
                                <FormControlLabel
                                  key={et.errorTagCodeId}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedErrorCodes.includes(et.errorTagCodeId)}
                                      onChange={(e) => {
                                        const newCodes = e.target.checked
                                          ? [...selectedErrorCodes, et.errorTagCodeId]
                                          : selectedErrorCodes.filter((id) => id !== et.errorTagCodeId);
                                        setSelectedErrorCodes(newCodes);

                                        let templateText = '';
                                        if (newCodes.length === 1) {
                                          const selected = errorTagCodes.find(c => c.errorTagCodeId === newCodes[0]);
                                          templateText = selected?.messageTemplate ?? '';
                                          setInsertTemplateValue(newCodes[0]);
                                        } else if (newCodes.length > 1) {
                                          const otherCode = findOtherErrorTemplateCode(errorTagCodes);
                                          templateText = otherCode?.messageTemplate ?? '';
                                          setInsertTemplateValue(otherCode?.errorTagCodeId ?? '');
                                        } else {
                                          setInsertTemplateValue('');
                                        }

                                        if (!templateText) {
                                          setLockedTemplateIntro("");
                                          setFieldValue("filerNotes", "", true);
                                          return;
                                        }
                                        const { lockedIntro, initialNotes } = splitTemplateForComposer(templateText);
                                        setLockedTemplateIntro(lockedIntro);
                                        setFieldValue("filerNotes", initialNotes, true);
                                      }}
                                    />
                                  }
                                  label={`${et.errorTagCodeId}: ${et.errorTagName}`}
                                  sx={{ display: 'flex', mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                />
                              ))}
                            </Grid>
                          ))}
                        </Grid>
                        {requiresErrorCode && selectedErrorCodes.length === 0 && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            Select at least one error code before sending the first message.
                          </Typography>
                        )}
                      </FormControl>
                    )}
                    <Box sx={replyComposerContainerSx}>
                      {lockedTemplateIntro ? (
                        <>
                          <Typography
                            component="div"
                            aria-readonly
                            sx={{
                              ...REPLY_COMPOSER_FONT,
                              px: "14px",
                              pt: "10px",
                              color: "text.primary",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              userSelect: "text",
                            }}
                          >
                            {lockedTemplateIntro}
                          </Typography>
                          <TextField
                            name="filerNotes"
                            multiline
                            minRows={8}
                            fullWidth
                            placeholder="Add filer notes here…"
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={values.filerNotes}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isReply()}
                            sx={{
                              px: "14px",
                              pt: 0.5,
                              pb: "10px",
                              flex: 1,
                              "& .MuiInputBase-root": { margin: 0, height: "100%" },
                              "& .MuiInputBase-input": {
                                ...REPLY_COMPOSER_FONT,
                                padding: 0,
                              },
                            }}
                          />
                        </>
                    ) : (
                      <TextField
                        name="filerNotes"
                        placeholder="Type your reply here..."
                        multiline
                        minRows={8}
                        fullWidth
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        value={values.filerNotes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isReply()}
                        sx={{
                          px: "14px",
                          py: "10px",
                          flex: 1,
                          "& .MuiInputBase-root": { margin: 0, height: "100%" },
                          "& .MuiInputBase-input": {
                            ...REPLY_COMPOSER_FONT,
                            padding: 0,
                          },
                        }}
                      />
                    )}
                    </Box>
                    {isReply() && touched.filerNotes && errors.filerNotes && (
                      <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                        {errors.filerNotes as string}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, mt: 2 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        size="medium"
                        onClick={onClose}
                        startIcon={<CloseOutlinedIcon />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderColor: "#e2e8f0",
                          color: "#475569",
                          "&:hover": {
                            borderColor: "#2099d2",
                            backgroundColor: "#2099d214",
                            color: "#2099d2",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        size="medium"
                        disabled={sendDisabled}
                        // disabled={isSubmitting || !composedReply.trim().length}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendOutlinedIcon />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          backgroundColor: (isSubmitting || !composedReply.trim().length) ? undefined : "#2099d2",
                          "&:hover": {
                            backgroundColor: (isSubmitting || !composedReply.trim().length) ? undefined : "#176990",
                          },
                          px: 2.5,
                        }}
                      >
                        {isSubmitting ? "Sending…" : isStaffUser(user?.userRoles) ? "Send & Return" : "Send"}
                      </Button>
                    </Box>
                  </div>
                )}
                {/* Close button for view only mode */}
                {viewOnly && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      size="medium"
                      onClick={onClose}
                      startIcon={<CloseOutlinedIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#475569",
                        "&:hover": {
                          borderColor: "#2099d2",
                          backgroundColor: "#2099d214",
                          color: "#2099d2",
                        },
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                )}
              </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
             {custPolicyID && custPolicyID !== 'undefined' && custPolicyID !== 'null' && custPolicyID !== '' && (
        <AddDocument open={openDoc} handleClose={handleClose} customerPolicyId={custPolicyID}></AddDocument>
       )}
    </>
  );
};

export default MessageDialog;